import React from 'react'
import classNames from 'classnames'
import { StyledListProps } from './StyledList.d'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import DisplayIcon from '@toasttab/shared/src/components/atoms/DisplayIcon/DisplayIcon'
import { Card } from '@toasttab/shared/src/utils/craftUtils'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { StyledListOptions } from '@toasttab/shared/src/utils/Contentful/richText'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import ToolTip from '@toasttab/shared/src/components/molecules/ToolTip/ToolTip'
import useIsClient from '@toasttab/shared/src/hooks/useIsClient'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'

const StyledList = ({
  classes,
  id,
  listItems = [],
  listOnly,
  listStyles = 'none',
  listVariation = 'default',
  sectionDescription,
  sectionHeading
}: StyledListProps) => {
  const { language, defaultLanguage, generateDefaultLanguagePage } =
    useI18next()
  const languageContext = {
    language,
    defaultLanguage,
    generateDefaultLanguagePage
  }
  const isClient = useIsClient()

  const listHeadingClasses = classNames({
    'text-gray-100': listVariation === 'heading-only',
    'text-default font-semibold block': listVariation === 'default'
  })

  const ulClasses = classNames('list-none [&>li:last-child]:mb-0', {
    'sm:inline-flex': listVariation === 'inline'
  })

  const listContainerClass = classNames(classes, 'relative')

  const renderList = () => {
    return listItems.map((list, index) => {
      const listInnerWrapperClass = classNames({
        'inline-flex self-center': list.tooltip || listVariation === 'inline'
      })
      const listClasses = classNames(
        'text-gray-100 leading-6 mb-4',
        Card.listStyle(list.listStyles || listStyles),
        {
          'pl-8 before:left-0 before:absolute before:top-0.5 before:w-6 before:h-6 before:block [&>span:nth-child(2)>a]:font-normal':
            listVariation !== 'inline' &&
            ((listStyles && listStyles !== 'none') ||
              (list.listStyles && list.listStyles !== 'none')),
          'sm:[&:not(:first-child)]:pl-8 before:mr-1 before:w-6 sm:before:float-left before:h-6 sm:before:block [&>span:nth-child(2)>a]:font-normal flex justify-center sm:block':
            listVariation === 'inline' &&
            ((listStyles && listStyles !== 'none') ||
              (list.listStyles && list.listStyles !== 'none'))
        }
      )
      return (
        <li
          className={listClasses}
          data-testid={`mds-styled-list-item-${index}`}
          key={`${id}-${index}`}
        >
          <div
            className={listInnerWrapperClass}
            data-testid={`mds-styled-list-item-${index}-inner`}
          >
            {list.displayIcon ? (
              <DisplayIcon
                classes='inline h-6 w-6 mr-3 mb-2 md:mb-0'
                iconName={list.displayIcon}
              />
            ) : null}
            {list.displayIconImage ? (
              <div className='inline-block h-6 w-6 mr-3 mb-2 md:mb-0 align-middle'>
                <Image {...list.displayIconImage} classes='' />
              </div>
            ) : null}
            <span
              className={listHeadingClasses}
              data-testid={`mds-styled-list-item-${index}-heading`}
            >
              {list.col1}
            </span>
            {list.col2 ? (
              list.col2.raw ? (
                <span>
                  {renderRichText(
                    list.col2,
                    StyledListOptions(list.listStyles, languageContext)
                  )}
                </span>
              ) : (
                <span dangerouslySetInnerHTML={{ __html: list.col2 }} />
              )
            ) : (
              ''
            )}
            {list.tooltip ? (
              <ToolTip
                tip={list.tooltip}
                tipContainerClass='mt-1 sm:mt-1 ml-1'
                uid={list.id}
                wide={list.tooltip.length > 150}
              />
            ) : null}
          </div>
        </li>
      )
    })
  }

  return (
    isClient && (
      <div className={listContainerClass} data-testid='mds-styled-list'>
        {!listOnly && (
          <>
            {sectionHeading ? (
              <Typography as='h5' classes='text-default mb-4' variation='h5'>
                {sectionHeading}
              </Typography>
            ) : null}
            {sectionDescription ? (
              <p className='text-gray-100 mb-4'>{sectionDescription}</p>
            ) : null}
          </>
        )}
        <ul className={ulClasses}>{renderList()}</ul>
      </div>
    )
  )
}

export default StyledList
